'use client';

import './gallery-raw.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.js';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselLazyLoadImage,
  CarouselNext,
  CarouselPrevious,
} from '../../../molecules/carousel/carousel';
import { FeatureBase } from '../../../molecules/feature-base/feature-base';
import { FeatureCard } from '../../../molecules/feature-card/feature-card';
import { HeaderGroup } from '../../../molecules/header-group/header-group';
import {
  Lightbox,
  LightboxTrigger,
} from '../../../molecules/lightbox/lightbox';
import { getPlaceholderItems } from '../../../utils/storybook';
import { getBaseSizeProps } from '../../../utils/text-utils';

export const GalleryRaw: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  items,
  alt,
  ...props
}) => {
  const { t } = useTranslation(['core', 'skippy']);

  const hasHeaderContent = !!props.title || !!props.body;
  const hasHeaderCard = hasHeaderContent && media?.image;
  const hasHeaderGroup = hasHeaderContent && !media?.image;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const galleryItems =
    !items || items.length < 4 ? getPlaceholderItems(items, 4) : items;

  return (
    <section
      className={cn('GalleryRaw component', {
        'GalleryRaw--alt': alt,
      })}
    >
      <div className="GalleryRaw__container container flex flex-col gap-comfy-xl lg:gap-roomy-xs">
        {hasHeaderCard && (
          <FeatureCard
            {...props}
            {...getBaseSizeProps(headerDisplayLevel)}
            seoLevel={seoLevel}
            media={media}
            imagePlacement="right"
            variant="horizontal"
            inline
          />
        )}
        {hasHeaderGroup && <HeaderGroup {...props} />}

        <Carousel
          aria-label={t('core:gallery.title', {
            title: props.title,
          })}
          opts={{
            align: 'start',
            loop: false,
            watchFocus: false,
          }}
        >
          <div className="GalleryRaw__wrapper flex flex-col gap-comfy-lg">
            <div className="GalleryRaw__carousel">
              <CarouselContent>
                {galleryItems.map((slide, index) => {
                  const hasButtons =
                    slide.primaryCTA || slide.secondaryCTA || slide.tertiaryCTA;
                  return (
                    <CarouselItem
                      key={['slide', index, slide.title].join('-')}
                      index={index}
                      className="GalleryRaw__slide"
                      aria-label={`${t('core:gallery.slide.title', {
                        index: index + 1,
                        length: galleryItems.length,
                      })}${slide.title && ` - ${slide.title}`}`}
                    >
                      <Lightbox
                        key={['lightbox', index, slide.title].join('-')}
                        items={galleryItems}
                        startIndex={index}
                      >
                        <LightboxTrigger
                          className="Lightbox__trigger w-full"
                          ariaLabel={t('core:gallery.open')}
                        >
                          <div className="GalleryRaw__image">
                            <CarouselLazyLoadImage
                              index={index}
                              media={slide.media}
                              isVideoThumbnail={!!slide.media?.video}
                            />
                          </div>
                        </LightboxTrigger>
                        <div className="mt-comfy-lg">
                          <FeatureBase
                            {...slide}
                            {...getBaseSizeProps(4)}
                            seoLevel={
                              hasHeaderContent ? seoLevel + 1 : seoLevel
                            }
                            media={undefined}
                            programmaticButtons={
                              slide.media?.video &&
                              slide.media.video.hasProgrammaticButton ? (
                                <LightboxTrigger
                                  className={cn('flex', {
                                    'ml-snug-xl': hasButtons,
                                  })}
                                >
                                  <LinkStyle
                                    iconBefore="play-circle"
                                    noUnderline
                                    inline
                                    size="md"
                                  >
                                    <span>{t('skippy:video.watch')}</span>
                                  </LinkStyle>
                                </LightboxTrigger>
                              ) : undefined
                            }
                            classNameHeaderSpacing="gap-snug-md lg:gap-snug-lg"
                            inline
                          />
                        </div>
                      </Lightbox>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </div>
            <div className="GalleryRaw__arrows flex gap-comfy-sm items-center justify-end">
              <CarouselPrevious />
              <CarouselNext />
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
};
